/*
 * RolePreviewBanner — sticky top-of-page banner that appears whenever the
 * admin is impersonating a custom role. Renders through <Layout /> so it
 * sits above every routed page.
 *
 * Behaviour:
 *   • Shows the role's display name + slug so the admin never forgets
 *     what lens they're looking through.
 *   • Explicit reminder that the backend still sees the admin's real
 *     credentials — this is a visual UI shim, not a security boundary.
 *   • "Exit preview" restores full permissions and drops the localStorage
 *     shim (see AuthContext.exitPreview).
 */
import { Eye, LogOut } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";

export function RolePreviewBanner() {
  const { previewRole, exitPreview } = useAuth();
  if (!previewRole) return null;

  const handleExit = () => {
    exitPreview();
    toast.success("Exited role preview");
  };

  return (
    <div
      data-testid="role-preview-banner"
      role="status"
      aria-live="polite"
      className="sticky top-0 z-50 border-b-2 border-[#FFCC00] bg-[#3B2A00] text-[#FFE066] shadow-[0_2px_16px_rgba(0,0,0,0.45)]"
    >
      <div className="px-4 sm:px-6 py-2.5 flex items-center gap-3 flex-wrap">
        <Eye className="size-4 shrink-0 text-[#FFCC00]" aria-hidden />
        <div className="text-xs sm:text-sm min-w-0 flex-1">
          <span className="uppercase tracking-widest font-black text-[#FFCC00]" data-testid="role-preview-label">
            Previewing as
          </span>{" "}
          <span
            className="font-display font-black uppercase text-white truncate"
            data-testid="role-preview-name"
            title={previewRole.slug}
          >
            {previewRole.display_name || previewRole.slug}
          </span>
          <span className="hidden sm:inline text-[#FFE066]/70 ml-2 text-[11px]">
            · UI is gated by this role — actions still hit the backend with your admin credentials
          </span>
        </div>
        <button
          type="button"
          data-testid="role-preview-exit"
          onClick={handleExit}
          className="uppercase tracking-widest text-[11px] font-black px-3 py-1.5 border border-[#FFCC00] text-[#FFCC00] hover:bg-[#FFCC00] hover:text-black transition-colors inline-flex items-center gap-1.5"
        >
          <LogOut className="size-3" /> Exit preview
        </button>
      </div>
    </div>
  );
}
